import { cn } from '#client/lib/cn';
import { markerIsStroke, markerPath, seriesColor, seriesMarker } from './series';
import type { Formatter, Series } from './types';

interface ChartTooltipProps {
  /** The active category, or undefined when nothing is. */
  activeKey: string | undefined;
  series: readonly Series[];
  format: Formatter;
  /** Where to put it, in plot pixels. */
  x: number;
  /** Plot width, so the card can flip rather than overflow. */
  width: number;
}

/**
 * The readout for whichever category the cursor is on.
 *
 * Absolutely positioned HTML rather than SVG `<text>`: it needs to wrap, use
 * the app's type scale and sit above the drawing, none of which SVG does well.
 *
 * `aria-hidden`, always. The same information reaches a screen reader through
 * the live region in `ChartFrame`, which speaks only when the cursor was moved
 * by a key — so a sighted mouse user's hover does not narrate itself, and a
 * keyboard reader is not told everything twice.
 */
export function ChartTooltip({ activeKey, series, format, x, width }: ChartTooltipProps) {
  if (activeKey === undefined) return null;

  const rows = series
    .map((one, index) => ({
      one,
      index,
      point: one.points.find((candidate) => candidate.key === activeKey),
    }))
    .filter((row) => row.point !== undefined);

  if (rows.length === 0) return null;
  const heading = rows[0]?.point?.label ?? activeKey;

  // Flip to the left of the cursor in the right-hand third, so the card stays
  // inside the plot instead of being clipped by the card that contains it.
  const flip = x > width * 0.66;

  return (
    <div
      className={cn('c-chart-tooltip', flip && 'c-chart-tooltip--flip')}
      style={{ insetInlineStart: `${x}px` }}
      aria-hidden="true"
    >
      <p className="c-chart-tooltip__heading">{heading}</p>
      <ul className="c-chart-tooltip__rows">
        {rows.map(({ one, index, point }) => {
          const marker = seriesMarker(index);
          const stroked = markerIsStroke(marker);
          return (
            <li key={one.id} className="c-chart-tooltip__row">
              <svg className="c-chart-tooltip__swatch" viewBox="-4 -4 8 8">
                <path
                  d={markerPath(marker, 6)}
                  fill={stroked ? 'none' : seriesColor(index, one.tone)}
                  stroke={seriesColor(index, one.tone)}
                  strokeWidth={stroked ? 1.5 : 0}
                />
              </svg>
              <span className="c-chart-tooltip__label">{one.label}</span>
              <span className="c-chart-tooltip__value tnum">
                {/* A gap is named, never printed as a zero — the whole reason
                    `Datum.value` is nullable. */}
                {point?.value == null ? 'No data' : format(point.value)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
